import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../Icon';
import T from '../components/EditableText';
import { useSiteContent } from '../SiteContent';
import useJsonLd from '../useJsonLd';
import { siteUrl } from '../sites';
import { track, M38_EVENTS } from './tracking';
import { TRANSKRIPTE, alsFliesstext } from './transkripte';

/**
 * Die vier Radio38-Beiträge zum Nachhören.
 *
 * Es sind keine Werbespots, sondern die vier Teile einer Sendung, die
 * aufeinander aufbauen (Teil 2 kündigt den Erfahrungsbericht in Teil 3 an).
 * Deshalb tragen die Kacheln „Teil 1…4" und stehen in fester Reihenfolge.
 *
 * Unter jedem Beitrag steht sein Transkript zum Aufklappen (`transkripte.ts`).
 * Es ist nicht Beiwerk: Ohne die Texte steht der ganze Inhalt der Sendung
 * nirgends auf der Seite — eine Suchmaschine liest keine MP3 —, und wer nicht
 * hören kann oder unterwegs keinen Ton hat, bekommt ihn gar nicht.
 *
 * Die Dateien liegen als statische Anhänge unter `public/website/audio/` und
 * werden von nginx ausgeliefert — nicht über die API. Sie ändern sich nach dem
 * Einpflegen nie wieder; eine Verwaltungsmaske dafür wäre Aufwand ohne Ertrag.
 *
 * `preload="none"` ist hier wichtig: Vier Beiträge sind zusammen rund zehn
 * Megabyte, und der überwiegende Teil der Besucher kommt über das Mobilfunknetz.
 * Geladen wird deshalb erst beim Antippen.
 *
 * Bewusst ein eigener Player statt `<audio controls>`: Der Systemplayer sieht in
 * jedem Browser anders aus, und die vier Beiträge sollen als Kacheln nebeneinander
 * stehen. Bedienbar bleibt er trotzdem — eine echte Schaltfläche mit
 * `aria-label`, und die Wiedergabe hängt an einem echten `<audio>`-Element.
 */

interface Beitrag {
  /** Nummer — bestimmt Datei (`beitrag-N.mp3`) und Textschlüssel. */
  nummer: number;
  /**
   * Länge in Sekunden, wie sie in der ausgelieferten Datei steht.
   *
   * Steht hier, weil `preload="none"` bedeutet, dass der Browser die Länge erst
   * kennt, wenn jemand abspielt — bis dahin stünde an jeder Kachel „–:––". Die
   * Angabe ist Teil der Entscheidung, ob sich jemand die Zeit nimmt, und darf
   * deshalb nicht erst nach dem Klick erscheinen.
   *
   * Beim Austausch einer Datei hier nachziehen (`ffmpeg -i <datei>`).
   */
  dauer: number;
}

const BEITRAEGE: Beitrag[] = [
  { nummer: 1, dauer: 208 },
  { nummer: 2, dauer: 202 },
  { nummer: 3, dauer: 210 },
  { nummer: 4, dauer: 214 },
];

/** Sekunden als `3:28`. */
const alsZeit = (sekunden: number): string => {
  if (!Number.isFinite(sekunden)) return '–:––';
  const min = Math.floor(sekunden / 60);
  const sek = Math.floor(sekunden % 60);
  return `${min}:${String(sek).padStart(2, '0')}`;
};

/** Sekunden als Dauer nach ISO 8601 (`PT3M28S`) — so will es Schema.org. */
const alsIsoDauer = (sekunden: number): string =>
  `PT${Math.floor(sekunden / 60)}M${Math.floor(sekunden % 60)}S`;

const AudioBeitraege: React.FC = () => {
  const { t } = useSiteContent();

  // Nur ein Beitrag darf klingen. Ohne das hier laufen zwei übereinander,
  // sobald jemand neugierig den nächsten antippt.
  const [laufend, setLaufend] = useState<number | null>(null);

  // Die Beiträge als strukturierte Daten, mitsamt Transkript: Google kann eine
  // MP3 nicht hören, und `transcript` ist das vorgesehene Feld, um ihm den
  // gesprochenen Inhalt trotzdem zu geben. Die Reihenfolge ist Teil der Sache,
  // deshalb eine ItemList und keine lose Sammlung.
  //
  // useMemo ist Pflicht: useJsonLd hängt an der Referenz und schriebe das
  // Script sonst bei jedem Rendern neu — und dieses rendert bei jedem Tastendruck
  // im Betreuungs-Check daneben.
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Radio38 „Gesundheit+" — Beiträge der Seniorenbetreuung Antczak',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: BEITRAEGE.map((beitrag) => ({
        '@type': 'ListItem',
        position: beitrag.nummer,
        item: {
          '@type': 'AudioObject',
          name: `Teil ${beitrag.nummer} — ${t(`m38.radio.beitrag-${beitrag.nummer}.titel`)}`,
          description: t(`m38.radio.beitrag-${beitrag.nummer}.text`),
          contentUrl: `${siteUrl()}/website/audio/beitrag-${beitrag.nummer}.mp3`,
          encodingFormat: 'audio/mpeg',
          duration: alsIsoDauer(beitrag.dauer),
          inLanguage: 'de-DE',
          transcript: alsFliesstext(beitrag.nummer),
        },
      })),
    }),
    [t],
  );
  useJsonLd('m38-radio', jsonLd);

  // 460px ergibt bei der Breite der Seite zwei Spalten — also 2x2 statt 3+1,
  // bei dem die vierte Kachel allein stehen bliebe. Auf dem Telefon fällt es
  // wie üblich auf eine Spalte zusammen.
  //
  // `align-items: start` statt der üblichen Streckung: Ein aufgeklapptes
  // Transkript macht seine Kachel mehrere hundert Pixel hoch, und die Nachbarin
  // zöge sonst als leerer Rahmen mit.
  return (
    <div className="autogrid" style={{ ['--min' as string]: '460px', alignItems: 'start' }}>
      {BEITRAEGE.map((beitrag) => (
        <AudioKachel
          key={beitrag.nummer}
          nummer={beitrag.nummer}
          bekannteDauer={beitrag.dauer}
          spielt={laufend === beitrag.nummer}
          onSpielen={() => setLaufend(beitrag.nummer)}
          onEnde={() => setLaufend((aktuell) => (aktuell === beitrag.nummer ? null : aktuell))}
        />
      ))}
    </div>
  );
};

const AudioKachel: React.FC<{
  nummer: number;
  bekannteDauer: number;
  spielt: boolean;
  onSpielen: () => void;
  onEnde: () => void;
}> = ({ nummer, bekannteDauer, spielt, onSpielen, onEnde }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [dauer, setDauer] = useState(bekannteDauer);
  const [position, setPosition] = useState(0);

  // Wird woanders auf Wiedergabe getippt, hält dieser Beitrag an.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!spielt && !audio.paused) audio.pause();
  }, [spielt]);

  /** Wiedergabe starten und dabei melden, dass hier jetzt gespielt wird. */
  const starten = (audio: HTMLAudioElement) => {
    onSpielen();
    // Ältere Browser geben kein Promise zurück, deshalb die Prüfung.
    const abspielen = audio.play();
    if (abspielen?.catch) abspielen.catch(() => onEnde());
  };

  const umschalten = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      starten(audio);
      track(M38_EVENTS.audio, { beitrag: nummer });
    } else {
      audio.pause();
      onEnde();
    }
  };

  /**
   * An die Stelle im Beitrag springen, an der der angetippte Absatz gesprochen
   * wird — der eigentliche Nutzen der Zeitmarken im Transkript.
   *
   * Wegen `preload="none"` ist beim ersten Antippen noch nichts geladen und
   * `currentTime` liefe ins Leere. Dann wird die Stelle gesetzt, sobald die
   * Kopfdaten da sind.
   */
  const springen = (sekunden: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const setzen = () => {
      audio.currentTime = sekunden;
      setPosition(sekunden);
    };
    if (audio.readyState >= 1) setzen();
    else audio.addEventListener('loadedmetadata', setzen, { once: true });
    if (audio.paused) starten(audio);
    track(M38_EVENTS.transkriptSprung, { beitrag: nummer, sekunde: sekunden });
  };

  const fortschritt = dauer > 0 ? (position / dauer) * 100 : 0;

  return (
    <article className="audio-card">
      <div className="audio-head">
        <button
          type="button"
          className="audio-play"
          onClick={umschalten}
          aria-label={spielt ? `Beitrag ${nummer} anhalten` : `Beitrag ${nummer} anhören`}
        >
          <Icon name={spielt ? 'pause' : 'play'} size={20} />
        </button>

        <div className="audio-body">
          <strong>
            <T k={`m38.radio.beitrag-${nummer}.titel`} />
          </strong>
          <p className="audio-text">
            <T k={`m38.radio.beitrag-${nummer}.text`} />
          </p>
          <span className="note">
            Teil {nummer} ·{' '}
            {spielt || position > 0 ? `${alsZeit(position)} von ${alsZeit(dauer)}` : alsZeit(dauer)}
          </span>
          {(spielt || position > 0) && (
            <div className="audio-bar" aria-hidden="true">
              <div style={{ width: `${fortschritt}%` }} />
            </div>
          )}

          <audio
            ref={audioRef}
            src={`/website/audio/beitrag-${nummer}.mp3`}
            preload="none"
            onLoadedMetadata={(e) => {
              // Ab jetzt gilt der gemessene Wert — falls die Datei doch einmal
              // ausgetauscht wurde, ohne die Angabe oben nachzuziehen.
              if (Number.isFinite(e.currentTarget.duration)) setDauer(e.currentTarget.duration);
            }}
            onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
            onEnded={() => {
              setPosition(0);
              onEnde();
            }}
            onPause={onEnde}
          />
        </div>
      </div>

      <Transkript nummer={nummer} onSpringen={springen} />
    </article>
  );
};

/**
 * Das Transkript eines Beitrags, zugeklappt.
 *
 * Zugeklappt, aber im Text: `<details>` versteckt den Inhalt nur optisch, im
 * ausgelieferten HTML steht er vollständig — genau darauf kommt es an, denn er
 * ist für Suchmaschinen der einzige Zugang zum Inhalt der Sendung. Aus
 * demselben Grund lädt er nicht erst beim Aufklappen nach.
 *
 * Der Text bekommt eine eigene Höhe mit eigenem Bildlauf: Ein Beitrag sind rund
 * dreieinhalb Minuten Gespräch, und aufgeklappt schöbe er sonst alles darunter
 * um mehrere Bildschirmhöhen weg. Damit das auch ohne Maus bedienbar bleibt,
 * ist der Bereich anfahrbar (`tabIndex`) und benannt.
 */
const Transkript: React.FC<{ nummer: number; onSpringen: (sekunden: number) => void }> = ({
  nummer,
  onSpringen,
}) => {
  const absaetze = TRANSKRIPTE[nummer] ?? [];
  if (absaetze.length === 0) return null;

  return (
    <details
      className="transkript"
      onToggle={(e) => {
        if (e.currentTarget.open) track(M38_EVENTS.transkript, { beitrag: nummer });
      }}
    >
      <summary>
        <Icon name="file-text" size={15} />
        <T k="m38.radio.transkript" />
      </summary>
      <div
        className="transkript-text"
        role="region"
        tabIndex={0}
        aria-label={`Transkript Teil ${nummer}`}
      >
        {absaetze.map((absatz) => (
          <p key={absatz.zeit} className={absatz.sprecher ? undefined : 'transkript-jingle'}>
            <button
              type="button"
              className="transkript-zeit"
              onClick={() => onSpringen(absatz.zeit)}
              title="An diese Stelle im Beitrag springen"
            >
              {alsZeit(absatz.zeit)}
            </button>
            {absatz.sprecher && <strong>{absatz.sprecher}: </strong>}
            {absatz.text}
          </p>
        ))}
      </div>
      <p className="note transkript-hinweis">
        <T k="m38.radio.transkript.hinweis" />
      </p>
    </details>
  );
};

export default AudioBeitraege;
